import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import QRCode from 'qrcode';
import { Copy, Check } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import { showToast } from '../Utils/toast';
import config from '../Utils/config';

const InviteModal = inject('MyProfileStore')(
  observer(({ MyProfileStore }) => {
    const { modalState, inviteCode, inviteCodeRequest } = MyProfileStore;
    const [qrCodeDataURL, setQrCodeDataURL] = useState(null);
    const [copyingLink, setCopyingLink] = useState(false);
    const [copyingCode, setCopyingCode] = useState(false);

    const inviteLink = inviteCode ? `${config.clientUrl}/join/${inviteCode}` : '';

    useEffect(() => {
      if (inviteCode && inviteLink) {
        QRCode.toDataURL(inviteLink, {
          width: 256,
          margin: 2,
          color: {
            dark: '#ffffff',
            light: '#0a0a0a',
          }
        })
          .then(url => setQrCodeDataURL(url))
          .catch(err => {
            console.error('QR code generation failed:', err);
            showToast.error('Failed to generate QR code');
          });
      } else {
        setQrCodeDataURL(null);
      }
    }, [inviteCode, inviteLink]);

    const handleClose = () => {
      MyProfileStore.closeInviteModal();
    };

    const handleGenerateInvite = async () => {
      try {
        await MyProfileStore.getInviteCode();
        showToast.success('Invite code generated!');
      } catch (error) {
        showToast.error('Failed to generate invite code');
      }
    };

    const copyToClipboard = async (text, type) => {
      const setCopying = type === 'link' ? setCopyingLink : setCopyingCode;

      try {
        // Modern Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          setCopying(true);
          showToast.success(`${type === 'link' ? 'Link' : 'Code'} copied to clipboard!`);
          setTimeout(() => setCopying(false), 2000);
        } else {
          // Fallback for older browsers
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          const success = document.execCommand('copy');
          document.body.removeChild(textarea);

          if (success) {
            setCopying(true);
            showToast.success(`${type === 'link' ? 'Link' : 'Code'} copied!`);
            setTimeout(() => setCopying(false), 2000);
          } else {
            throw new Error('Copy failed');
          }
        }
      } catch (error) {
        showToast.error('Failed to copy to clipboard');
      }
    };

    return (
      <Modal
        isOpen={modalState.invite}
        onClose={handleClose}
        title="Invite a Friend"
        size="md"
      >
        <div className="space-y-6">
          {/* Loading State */}
          {inviteCodeRequest && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-accent" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {/* No Invite - Show Generate Button */}
          {!inviteCodeRequest && !inviteCode && (
            <div className="text-center py-6 space-y-4">
              <p className="text-sm text-gray-400">
                You don't have any active invite codes.
              </p>
              <Button onClick={handleGenerateInvite} variant="primary" size="md">
                Generate Invite Code
              </Button>
            </div>
          )}

          {/* Has Invite - Show Code, Link, and QR */}
          {!inviteCodeRequest && inviteCode && (
            <>
              {/* Invite Code Section */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text">
                  Invitation Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteCode}
                    readOnly
                    className="
                      flex-1 px-3 py-2 bg-secondary border border-neutral-700 rounded-lg text-text text-center font-mono text-lg focus:outline-none focus:ring-2 focus:ring-accent
                    "
                  />
                  <button
                    onClick={() => copyToClipboard(inviteCode, 'code')}
                    className="
                      px-3 py-2 bg-accent text-white rounded-lg hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed
                    "
                    aria-label="Copy invitation code"
                  >
                    {copyingCode ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
              </div>

              {/* Invite Link Section */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text">
                  Invitation Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="
                      flex-1 px-3 py-2 bg-secondary border border-neutral-700 rounded-lg text-text text-sm focus:outline-none focus:ring-2 focus:ring-accent overflow-x-auto
                    "
                  />
                  <button
                    onClick={() => copyToClipboard(inviteLink, 'link')}
                    className="
                      px-3 py-2 bg-accent text-white rounded-lg hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed
                    "
                    aria-label="Copy invitation link"
                  >
                    {copyingLink ? <Check size={20} /> : <Copy size={20} />}
                  </button>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text text-center">
                  QR Code
                </label>
                <div className="flex justify-center">
                  {qrCodeDataURL ? (
                    <div className="p-4 bg-primary border border-neutral-700 rounded-lg">
                      <img
                        src={qrCodeDataURL}
                        alt="Invite QR Code"
                        className="w-64 h-64"
                      />
                    </div>
                  ) : (
                    <div className="w-64 h-64 flex items-center justify-center bg-secondary rounded-lg border border-neutral-700">
                      <p className="text-sm text-gray-400">Generating QR code...</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-center text-gray-400">
                  Scan this code to join Muzio
                </p>
              </div>

              {/* Info Note */}
              <div className="bg-accent bg-opacity-10 bg-opacity-10 border-2 border-accent rounded-lg p-4">
                <p className="text-sm text-text">
                  This invite code can be used once. After someone signs up with it, you'll be able to generate a new one.
                </p>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  })
);

export default InviteModal;
